import { useMemo, useState } from "react";
import { Alert } from "react-bootstrap";
import { useHistory } from "react-router-dom";

import { useGetRallies } from "../../../../../api/jeep-rally/rally/rally.hooks";
import { useCreateRouteMutation } from "../../../../../api/jeep-rally/routes/routes.hooks";
import { isJeepRallyAdmin } from "../../Categories/utils/category.utils";
import { normalizeRallies } from "../../Rallies/utils/rally.utils";
import RouteForm from "../_components/RouteForm";
import {
  buildRoutePayload,
  getRouteApiErrorMessage,
  initialRouteForm,
  validateRouteForm,
} from "../utils/route.utils";

const RouteCreatePage = () => {
  const history = useHistory();
  const canManage = isJeepRallyAdmin();

  const [form, setForm] = useState(initialRouteForm);
  const [validationErrors, setValidationErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  const {
    mutateAsync: createRoute,
    isLoading,
    isPending,
    reset,
  } = useCreateRouteMutation();

  const { data: ralliesData, isLoading: isRalliesLoading } = useGetRallies();
  const rallies = useMemo(
    () => normalizeRallies(ralliesData),
    [ralliesData]
  );

  const submitting = isLoading || isPending;

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setValidationErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canManage) return;

    reset();
    setSubmitError("");

    const nextErrors = validateRouteForm(form);
    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await createRoute({
        payload: buildRoutePayload(form, { isEdit: false }),
      });
      history.push("/jeep-rally/routes");
    } catch (error) {
      setSubmitError(
        getRouteApiErrorMessage(error, "Failed to create route.")
      );
    }
  };

  const goBack = () => history.push("/jeep-rally/routes");

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to routes list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <h4 className="card-title mb-0">Create Route</h4>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Routes are standalone. Categories link to a route via{" "}
            <code>route_id</code>. Stages are optional and can attach to a
            rally later. Images are uploaded to gallery first.
          </p>

          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to create routes.
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          <RouteForm
            form={form}
            validationErrors={validationErrors}
            submitting={submitting}
            submitLabel="Create Route"
            submittingLabel="Creating..."
            readOnly={!canManage}
            rallies={rallies}
            isRalliesLoading={isRalliesLoading}
            onSubmit={handleSubmit}
            onFieldChange={updateField}
            onBack={goBack}
          />
        </div>
      </div>
    </div>
  );
};

export default RouteCreatePage;
